import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import useGet from "../../../../services/hooks/useGet";
import { times } from "../../../../shared/constants/all-constant";
import { apiEndpoints } from "../../../../shared/constants/api-constant";
import { Heading } from "../../../../shared/ReusableComponents";
import MedicationsPopup from "./MedicationPopup";
import { IFrequency, IMedication, IMedicationHistory } from "./types";
import { MedicationHistory } from "./MedicationHistory";
import { AppConfigurationHelper } from "../../../../services/AppConfigurationHelper";
import { caseRepository } from "../../../../services/caseRepository";
import { DateFormatter } from "../../../../shared/utils/DateFormatter";
import { useDispatch } from "react-redux";
import {
  hideLoader,
  showLoader,
} from "../../../../store/currentPreferencesSlice";

function Prescribed({
  caseId,
  medicineType,
}: {
  caseId: number;
  medicineType: string;
}) {
  const [prescribeData, setPrescribeData] = useState<any>([]);
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [medicationHistoryData, setMedicationHistoryData] = useState<any>([]);
  const { error, data, getService, loading } = useGet();
  const TIME_SLOTS = AppConfigurationHelper.Instance().timeSlots;
  const dispatch = useDispatch();

  const loadData = () => {
    getService(`${apiEndpoints.MEDICATIONS}/${caseId}`);
  };

  const loadHistory = () => {
    caseRepository.GetMedicationHistory(caseId).then((result: any) => {
      const today = moment().format("YYYY-MM-DD");
      const res = [...result];
      const filteredResult = res.filter(
        (item) =>
          item.medicineType === medicineType &&
          moment(item.startDate).format("YYYY-MM-DD") === today
      );
      setMedicationHistoryData(filteredResult);
    });
  };

  useEffect(() => {
    loading && dispatch(showLoader());
    !loading && dispatch(hideLoader());
  }, [loading]);

  useEffect(() => {
    if (!caseId || error) return;
    if (!(data && prescribeData)) {
      loadData();
    }
    loadHistory();
  }, [caseId, medicineType]);

  useEffect(() => {
    setIsHistoryVisible(false);
    if (data) {
      const res = [...data];
      const result = res
        .filter((item) => item.medicineType === medicineType)
        .map((item: any) => {
          const maxPRN = item.maxPRN ? `/Max ${item.maxPRN}` : "";
          return {
            ...item,
            describe: `${item.medicineType}/${item.route}/${item.dose} ${item.frequencyName} ${maxPRN}`,
          };
        });
      setPrescribeData(result);
    }
  }, [data, medicineType]);

  const date = moment().format("DD MMM YYYY");

  const GetOptionsToGiveOrGivenText = (
    frequency: IFrequency,
    medication: IMedication,
    medHistory: IMedicationHistory[]
  ) => {
    const matchedHistory = medHistory.find(
      (history) => history.scheduleTimeId === frequency.doseDetails.id
    );

    const finalIsGiven = matchedHistory?.status ?? frequency.isGiven;
    const signedBy = matchedHistory?.signedBy ?? frequency.doseDetails.signedBy;
    const dose = matchedHistory?.dose ?? frequency.doseDetails.dose;
    const rawSchedule =
      matchedHistory?.schedule ?? frequency.doseDetails.scheduleTime;
    const formattedSchedule = rawSchedule
      ? DateFormatter.formatTime(moment(rawSchedule, "HH:mm:ss").toISOString())
      : "";
    const reason = matchedHistory?.reason ?? frequency.doseDetails.reason;
    const startDate = matchedHistory?.startDate
      ? DateFormatter.formatDate(matchedHistory?.startDate)
      : "";

    return finalIsGiven === null ? (
      <MedicationsPopup
        frequency={frequency}
        medication={medication}
        loadData={loadData}
        loadHistory={loadHistory}
      />
    ) : finalIsGiven === true ? (
      <Heading
        type="body1"
        isBold={true}
        text={`${startDate}, ${formattedSchedule}, ${dose} Administered by ${signedBy}`}
      />
    ) : (
      <React.Fragment>
        <Heading
          type="body1"
          isBold={true}
          text={`Not Administered by ${signedBy}`}
        />
        <Heading type="body2" text={`Reason: ${reason}`} />
      </React.Fragment>
    );
  };

  const getBackgroundColor = (schedule: string) => {
    let currentTime = moment();
    let endTime = moment(schedule, "HH:mm:ss");
    let beforeTime = endTime
      .clone()
      .subtract(
        AppConfigurationHelper.Instance().medicineIntervalAllowed,
        "minutes"
      );
    let afterTime = endTime
      .clone()
      .add(
        AppConfigurationHelper.Instance().medicineIntervalAllowed,
        "minutes"
      );
    if (currentTime.isAfter(afterTime)) return "var(--color-warning-light-box)";
    else if (currentTime.isBefore(beforeTime))
      return "var(--color-info-light-box)";
    else if (currentTime.isBetween(beforeTime, afterTime, undefined, "[]"))
      return "var(--color-success-dark-box)";
    return "transparent";
  };

  return (
    <>
      <Box className="d-flex justify-between mb-1">
        {isHistoryVisible ? (
          <Box
            className="d-flex align-items-center cursor-pointer"
            sx={{ ml: "20px", alignItems: "center" }}
          >
            <span
              className="material-symbols-outlined primary-color"
              style={{ cursor: "pointer", color: "var(--color-primary-main)" }}
              onClick={() => setIsHistoryVisible(false)}
            >
              arrow_back
            </span>
            <Heading type="h5" isBold={true} text="Medication History" />
          </Box>
        ) : (
          <Heading type="h5" isBold={true} text="Prescription" />
        )}
        {!isHistoryVisible && (
          <Heading
            classes="navigate cursor-pointer"
            type="caption"
            text="Medication History"
            onClick={() => setIsHistoryVisible(true)}
          />
        )}
      </Box>
      {isHistoryVisible ? (
        <MedicationHistory caseId={caseId} medicationType={medicineType} />
      ) : (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: "auto", overflow: "auto" }}>
            <Table stickyHeader size="small" aria-label="a dense table">
              <TableHead>
                <TableRow>
                  <TableCell className="bg-default pxxs">
                    <Heading text={date} type={"h5"} isBold={true} />
                  </TableCell>
                  {times.map((time) => (
                    <TableCell
                      className="bg-default pxxs"
                      key={time.key}
                      align="center"
                    >
                      <Heading text={time.key} type={"h5"} isBold={true} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {prescribeData.length > 0 ? (
                  prescribeData.map((med: any) => (
                    <TableRow key={med.id}>
                      <TableCell className="bg-default borderBox pxxs w-15rem">
                        <Heading
                          text={med.medicineName}
                          type={"h5"}
                          color="var(--color-primary-main)"
                          isBold={true}
                        />
                        <Heading
                          text={med.describe}
                          type={"body1"}
                          style={{ display: "inline-block" }}
                        />
                        <br />
                        <Heading
                          text="Indication : "
                          type={"body1"}
                          style={{ display: "inline-block" }}
                          isBold={true}
                        />
                        <Heading
                          text={med.indication}
                          type={"body1"}
                          style={{ display: "inline-block", paddingLeft: 3 }}
                        />
                        <br />
                        <Heading
                          text="Prescribed by : "
                          type={"body1"}
                          style={{ display: "inline-block" }}
                          isBold={true}
                        />
                        <Heading
                          text={med.prescribedby}
                          type={"body1"}
                          style={{ display: "inline-block", paddingLeft: 3 }}
                        />
                      </TableCell>
                      {times.map((time) => {
                        let frequenciesInSlot: any[] = [];

                        const isVariableMedicine =
                          med.medicineType?.toLowerCase() === "variable dose" &&
                          med.frequencyName?.toLowerCase() === "variable";

                        if (isVariableMedicine) {
                          // Use timeSlots for grouping
                          frequenciesInSlot = med.frequency.filter(
                            (fre: any) => {
                              const scheduleTime = moment(
                                fre.schedule,
                                "HH:mm:ss"
                              );

                              const matchedSlot = TIME_SLOTS.find(
                                (slot: any) => {
                                  const start = moment(slot.start, "HH:mm");
                                  const end = moment(slot.end, "HH:mm");

                                  return scheduleTime.isBetween(
                                    start,
                                    end,
                                    null,
                                    "[]"
                                  );
                                }
                              );

                              return (
                                matchedSlot?.label === time.key.replace(":", "")
                              );
                            }
                          );
                        } else {
                          // Use exact matching with `times`
                          frequenciesInSlot = med.frequency.filter(
                            (fre: any) => {
                              const scheduleFormatted = moment(
                                fre.schedule,
                                "HH:mm:ss"
                              ).format("HH:mm");
                              if (
                                fre.schedule === "23:59:59" &&
                                time.key === "24:00"
                              ) {
                                return true;
                              }
                              return scheduleFormatted === time.key;
                            }
                          );
                        }

                        return (
                          <TableCell
                            key={time.key}
                            align="left"
                            className="borderBox pxxxs"
                            style={{
                              backgroundColor:
                                frequenciesInSlot.length > 0
                                  ? frequenciesInSlot.some((f: any) => {
                                      const matchedHistory =
                                        medicationHistoryData.find(
                                          (h: any) =>
                                            h.scheduleTimeId ===
                                            f.doseDetails.id
                                        );
                                      const isGivenFromHistory =
                                        matchedHistory?.status;
                                      return (
                                        [true, false].includes(f.isGiven) ||
                                        isGivenFromHistory !== undefined
                                      );
                                    })
                                    ? "var(--color-light-grey)"
                                    : getBackgroundColor(
                                        frequenciesInSlot[0].schedule
                                      )
                                  : "transparent",
                            }}
                          >
                            {frequenciesInSlot.map(
                              (f: IFrequency, i: number) => (
                                <Box key={i}>
                                  {GetOptionsToGiveOrGivenText(
                                    f,
                                    med,
                                    medicationHistoryData
                                  )}
                                </Box>
                              )
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{ height: "90px" }}>
                    <TableCell
                      className="borderBox pxxs"
                      colSpan={times.length + 1}
                      align="center"
                    >
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        fontSize="1rem"
                        color={"var(--color-border-model)"}
                      >
                        No data available.
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </>
  );
}

export default Prescribed;
